import React, { useMemo } from 'react';
import { isNil } from 'lodash';
import { useSelector } from 'react-redux';
import {
  components,
  OptionProps,
  SingleValue,
  SingleValueProps,
} from 'react-select';
import ReactSelectMenu from '../../Dropdown/StyledReactSelect/ReactSelectMenu';
import { RootState } from '../../../redux/store';
import { colors } from '../../../config/styles';
import {
  ALL_INCIDENTS_OPTION_NAME,
  ALL_INCIDENTS_OPTION_VALUE,
  IncidentType,
} from '../../IncidentType';
import { BasicDropdownIndicator } from '../../Dropdown/StyledReactSelect/SelectUtils';
import { IReactSelectOption } from '../../../utils/types';
import { theme } from '../../../styles/theme';

interface IProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  includeAllIncidents?: boolean;
  hasError?: boolean;
}

const IncidentTypeMenuLabel = (
  props: SingleValueProps<IReactSelectOption, false>,
) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <IncidentType name={data.label} />
    </components.SingleValue>
  );
};

const IncidentTypeMenuOption = (
  props: OptionProps<IReactSelectOption, false>,
) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <IncidentType name={data.label} />
    </components.Option>
  );
};

export default ({
  value,
  onChange,
  placeholder,
  includeAllIncidents,
  hasError,
}: IProps) => {
  const incidentTypes = useSelector(
    (state: RootState) => state?.incidents?.incidentTypes,
  );

  const options: IReactSelectOption[] = useMemo(() => {
    const result = [];

    if (includeAllIncidents) {
      result.push({
        value: ALL_INCIDENTS_OPTION_VALUE,
        label: ALL_INCIDENTS_OPTION_NAME,
      });
    }

    if (isNil(incidentTypes)) {
      return result;
    }

    Object.values(incidentTypes).forEach((incidentType) => {
      result.push({
        value: incidentType.id,
        label: incidentType.name,
      });
    });

    return result;
  }, [incidentTypes, includeAllIncidents]);

  const findSelectedOption = (options: IReactSelectOption[]) => {
    return options.find((option) => option.value === value) || null;
  };

  const handleChange = (option: SingleValue<IReactSelectOption>) => {
    if (option?.value) {
      onChange(option.value);
    }
  };

  return (
    <ReactSelectMenu
      value={findSelectedOption(options)}
      onChange={handleChange}
      placeholder={placeholder}
      options={options}
      components={{
        DropdownIndicator: (props) =>
          BasicDropdownIndicator({ props, color: theme.colors.secondary900 }),
        SingleValue: IncidentTypeMenuLabel,
        Option: IncidentTypeMenuOption,
      }}
      styles={{
        control: (base, prop) => {
          if (hasError && !prop.menuIsOpen) {
            return {
              ...base,
              borderColor: colors.mainError,
            };
          }

          return base;
        },
        valueContainer: (base) => ({
          ...base,
          padding: '2px 3px 2px 5px',
        }),
      }}
      isMulti={false}
      closeMenuOnSelect
    />
  );
};
